import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  Slide,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { forwardRef } from 'react';

// Transition для мобильных - снизу вверх
const SlideTransition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * FormDialog - модальное окно для форм в стиле MD3
 * 
 * @param {boolean} open - открыто ли окно
 * @param {function} onClose - обработчик закрытия
 * @param {string} title - заголовок
 * @param {string} subtitle - подзаголовок
 * @param {ReactNode} children - содержимое (форма)
 * @param {ReactNode} actions - кнопки действий (если не указано, используются default)
 * @param {function} onSubmit - обработчик отправки
 * @param {string} submitText - текст кнопки отправки
 * @param {string} cancelText - текст кнопки отмены
 * @param {boolean} loading - состояние загрузки
 * @param {string} loadingText - текст кнопки во время загрузки
 * @param {boolean} disableSubmit - отключить кнопку отправки
 * @param {string} maxWidth - максимальная ширина: xs, sm, md, lg, xl
 * @param {boolean} fullScreen - на весь экран (auto для мобильных)
 * @param {boolean} hideActions - скрыть кнопки действий
 */
export default function FormDialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  onSubmit,
  submitText = 'Сохранить',
  cancelText = 'Отмена',
  loading = false,
  loadingText = 'Сохранение…',
  disableSubmit = false,
  maxWidth = 'sm',
  fullScreen = 'auto',
  hideActions = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const isFullScreen = fullScreen === true || (fullScreen === 'auto' && isMobile);
  
  const handleSubmit = (event) => {
    event?.preventDefault();
    onSubmit?.();
  };
  
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth={maxWidth}
      fullWidth
      fullScreen={isFullScreen}
      TransitionComponent={isFullScreen ? SlideTransition : undefined}
      PaperProps={{
        sx: {
          borderRadius: isFullScreen ? 0 : 3,
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          px: { xs: 2.5, sm: 3 },
          pt: { xs: 2.5, sm: 3 },
          pb: subtitle ? 1.5 : 2,
        }}
      >
        <Box>
          <Typography variant="headlineSmall" component="h2">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="bodyMedium" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        
        <IconButton
          onClick={onClose}
          disabled={loading}
          size="small"
          aria-label="Закрыть"
          sx={{ flexShrink: 0 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      {/* Content */}
      <DialogContent
        sx={{
          px: { xs: 2.5, sm: 3 },
          // Outlined labels выступают над рамкой поля. Явный отступ не даёт
          // первому label попасть под верхнюю границу scroll-контейнера.
          pt: '16px !important',
          pb: 1,
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {children}
        </Box>
      </DialogContent>
      
      {/* Actions */}
      {!hideActions && (
        <DialogActions
          sx={{
            gap: 1,
            px: { xs: 2.5, sm: 3 },
            pt: 2,
            pb: { xs: 2.5, sm: 3 },
          }}
        >
          {actions || (
            <>
              <Button
                onClick={onClose}
                disabled={loading}
                color="inherit"
              >
                {cancelText}
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={loading || disableSubmit}
              >
                {loading ? loadingText : submitText}
              </Button>
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}
